import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MapPin, Phone, Facebook, Instagram, MessageSquare, Star } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_dd86d20d-7215-40c9-ab96-b556c05959d7/artifacts/n0vf802x_Plan%20de%20travail%2040.png";

export default function Footer() {
  const { t } = useTranslation();

  const values = [
    t('footerExt.value1'),
    t('footerExt.value2'),
    t('footerExt.value3'),
    t('footerExt.value4'),
    t('footerExt.value5'),
    t('footerExt.value6')
  ];

  const reviews = [
    { name: "Marie L.", rating: 5, text: t('footerExt.review1') },
    { name: "Pierre D.", rating: 5, text: t('footerExt.review2') },
    { name: "Sophie M.", rating: 5, text: t('footerExt.review3') },
  ];

  return (
    <footer data-testid="footer" className="bg-zinc-950 text-white pt-20 pb-8">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Avis Section */}
        <div className="mb-12 pb-12 border-b border-zinc-800">
          <h4 className="font-['Playfair_Display'] text-xl font-bold text-center mb-8">
            {t('footerExt.visitorsSay')}
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {reviews.map((review, index) => (
              <div key={index} className="bg-zinc-900 rounded-xl p-6 text-center">
                <div className="flex justify-center gap-1 mb-3">
                  {[...Array(review.rating)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <p className="text-zinc-300 text-sm italic mb-3">"{review.text}"</p>
                <p className="text-zinc-500 text-xs font-medium">{review.name}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          {/* Logo & Description */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-6">
              <img src={LOGO_URL} alt="Brious'O Folies" className="h-20 w-auto" />
            </Link>
            <p className="text-zinc-400 text-sm leading-relaxed mb-6">
              {t('footerExt.description')}
            </p>
            <div className="flex gap-4">
              <a
                href="https://www.facebook.com/profile.php?id=61575825750948"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 bg-zinc-800 rounded-full hover:bg-fuchsia-600 transition-colors"
                data-testid="footer-facebook"
                aria-label="Facebook Brious'O Folies"
              >
                <Facebook className="h-5 w-5" />
              </a>
              <a
                href="https://www.instagram.com/briousofolies/"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 bg-zinc-800 rounded-full hover:bg-fuchsia-600 transition-colors"
                data-testid="footer-instagram"
                aria-label="Instagram Brious'O Folies"
              >
                <Instagram className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="font-['Playfair_Display'] text-lg font-bold mb-6">{t('footerExt.navigation')}</h4>
            <nav className="flex flex-col gap-3">
              <Link to="/" className="text-zinc-400 hover:text-fuchsia-400 transition-colors">{t('footerExt.home')}</Link>
              <Link to="/evenements" className="text-zinc-400 hover:text-fuchsia-400 transition-colors">{t('footerExt.events')}</Link>
              <Link to="/association" className="text-zinc-400 hover:text-fuchsia-400 transition-colors">{t('footerExt.association')}</Link>
              <Link to="/venir" className="text-zinc-400 hover:text-fuchsia-400 transition-colors">{t('footerExt.gettingHere')}</Link>
              <Link to="/actualites" className="text-zinc-400 hover:text-fuchsia-400 transition-colors">{t('footerExt.news')}</Link>
              <Link to="/benevoles" className="text-zinc-400 hover:text-fuchsia-400 transition-colors">{t('footerExt.volunteers')}</Link>
              <Link to="/partenaires" className="text-zinc-400 hover:text-fuchsia-400 transition-colors">{t('footerExt.partners')}</Link>
              <Link to="/contact" className="text-zinc-400 hover:text-fuchsia-400 transition-colors">{t('footerExt.contactLabel')}</Link>
            </nav>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-['Playfair_Display'] text-lg font-bold mb-6">{t('footerExt.contactTitle')}</h4>
            <div className="flex flex-col gap-4">
              <div className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-lime-500 flex-shrink-0 mt-0.5" />
                <span className="text-zinc-400 text-sm">
                  Domaine de la Briouse<br />
                  La Briouse<br />
                  86600 Sanxay
                </span>
              </div>
              <Link
                to="/contact"
                className="flex items-center gap-3 text-zinc-400 hover:text-fuchsia-400 transition-colors"
              >
                <MessageSquare className="h-5 w-5 text-lime-500" />
                <span className="text-sm">{t('footerExt.contactUsLink')}</span>
              </Link>
              <a
                href="tel:+33749513090"
                className="flex items-center gap-3 text-zinc-400 hover:text-fuchsia-400 transition-colors"
              >
                <Phone className="h-5 w-5 text-lime-500" />
                <span className="text-sm">07 49 51 30 90</span>
              </a>
            </div>
          </div>

          {/* Values */}
          <div>
            <h4 className="font-['Playfair_Display'] text-lg font-bold mb-6">{t('footerExt.ourValues')}</h4>
            <div className="flex flex-wrap gap-2">
              {values.map((value, index) => (
                <span
                  key={index}
                  className="px-3 py-1 text-xs bg-zinc-800 text-zinc-300 rounded-full"
                >
                  {value}
                </span>
              ))}
            </div>
            <Link
              to="/adhesion"
              className="mt-6 inline-flex items-center gap-2 btn-primary text-sm"
              data-testid="footer-adhesion-btn"
            >
              {t('footerExt.becomeMember')}
            </Link>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-zinc-800 pt-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-zinc-500 text-sm">
              © {new Date().getFullYear()} Brious'O Folies. {t('footerExt.allRights')}.
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-zinc-500 text-sm">
              <Link to="/mentions-legales" className="hover:text-fuchsia-400 transition-colors">
                {t('footerExt.legalNotice')}
              </Link>
              <span>|</span>
              <Link to="/mentions-legales?tab=cgv" className="hover:text-fuchsia-400 transition-colors">
                {t('footerExt.cgv')}
              </Link>
              <span>|</span>
              <Link to="/mentions-legales?tab=confidentialite" className="hover:text-fuchsia-400 transition-colors">
                {t('footerExt.privacy')}
              </Link>
            </div>
            <p className="text-zinc-500 text-sm">
              Domaine de la Briouse - Sanxay (86)
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
